import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ExecutionEnvironment from 'exenv';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';
import { parse } from 'query-string';
import appendQuery from 'append-query';

import { SEARCH_COLOR_SWATCHES_COOKIE } from 'constants/cookies';
import { evRecommendationImpressionWrapper } from 'events/recommendations';
import { HYDRA_ADS, HYDRA_BANNER_ADS_SEARCH, HYDRA_SEARCH_INLINE_VERTICAL_RECOS } from 'constants/hydraTests';
import marketplace from 'cfg/marketplace.json';
// constants & regex
import {
  NON_SEARCH_PREFIXES,
  PRODUCT_ASIN,
  SEO_URL_RE,
  SLASH_SEARCH_RE,
  ZSO_URL_RE,
  ZSO_URL_WITH_FILTERS_RE
} from 'common/regex';
// actions
import { setError } from 'actions/errors';
import { pageTypeChange } from 'actions/common';
import { setHFSearchTerm } from 'actions/headerfooter';
import { redirectWithAppRoot } from 'actions/redirect';
import { sessionExpiration, setAndStoreCookie } from 'actions/session';
import { decorateSearchResponse, setOosMessaging } from 'actions/products';
import { getHeartCounts, getHearts } from 'actions/hearts';
import { fetchFromSearch, fetchFromZso, processReceivedSearchResponse } from 'actions/fancyUrls';
import {
  clearInlineRecos,
  clearInlineVerticalRecos,
  fetchSearchInlineRecos,
  fetchSearchInlineVerticalRecos,
  toggleFacetableView,
  toggleFacetsContainer
} from 'actions/search';
import { fetchSponsoredProducts, fetchSymphonySearchComponents } from 'store/ducks/search/actions';
import NoSearchResults from 'components/search/NoSearchResults';
// resources
import { productBundle } from 'apis/cloudcatalog';
import { track } from 'apis/amethyst';
import { logDebug } from 'middleware/logger';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { FetchError, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import ProductUtils from 'helpers/ProductUtils';
import { isDesktop } from 'helpers/ClientUtils';
import { stripAppRoot } from 'history/AppRootUtils';
import {
  isPrettySearchOrSlashSearchPath,
  locationLooksLikeNonNullQueryStringSearch,
  lookLikeSameSearchUrls,
  makeSearchUrl,
  normalizeSearchLocation,
  searchUrlMatchesFilters,
  shouldSearchServiceBeCalled
} from 'helpers/SearchUtils';
import { isAssigned, triggerAssignment } from 'actions/ab';

// marketplace
const {
  search: { hasInfinitePagination, showHorizontalInlineSearchRecos, showVerticalInlineSearchRecos, oosMessaging },
  checkout: { allowMoveToFavorites },
  hasAds,
  hasBannerAds
} = marketplace;

export class SearchLogic extends Component {
  static build404ErrorMessage = () => setError('404 not found', new Error('Not a Search URL.'), 404);

  static beforeFetchDataOnServer({ dispatch }) {
    dispatch(triggerAssignment(HYDRA_ADS));
    dispatch(triggerAssignment(HYDRA_BANNER_ADS_SEARCH));
  }

  static fetchDataOnServer(store, location, params, { zsoFetch, searchFetch, redirect, asinFetch } = {}) {
    // default params do not work in static functions?
    searchFetch = searchFetch || fetchFromSearch;
    zsoFetch = zsoFetch || fetchFromZso;
    redirect = redirect || redirectWithAppRoot;
    asinFetch = asinFetch || productBundle;
    const qs = location.pathname === '/search' ? parse(location.search) : {};
    const potentialAsin = (params.seoName || qs.term || '').toUpperCase();
    if (ZSO_URL_RE.test(location.pathname)) {
      if (NON_SEARCH_PREFIXES.test(location.pathname)) {
        return store.dispatch(SearchLogic.build404ErrorMessage());
      } else {
        return store.dispatch(zsoFetch({ location, keepParams: true }));
      }
    } else if (PRODUCT_ASIN.test(potentialAsin)) {
      const state = store.getState();
      const { environmentConfig: { api: { cloudcatalog: cloudcatalogInfo } } } = state;
      return asinFetch(cloudcatalogInfo, { asin: potentialAsin })
        .then(fetchErrorMiddleware)
        .then(response => {
          const productReceived = response.statusCode === '200' && response.product.length;
          if (productReceived) {
            const productUrl = ProductUtils.getProductUrlFromAsin(response.product[0], potentialAsin);
            if (productUrl) {
              return store.dispatch(redirect(productUrl));
            }
          }
          throw new FetchError(location.pathname, response.statusCode, `Unexpected CloudCatalogAPI response for ASIN: ${potentialAsin}`);
        })
        .catch(() => {
          if (isPrettySearchOrSlashSearchPath(location.pathname)) {
            return store.dispatch(searchFetch({ location: normalizeSearchLocation(location), keepParams: true }));
          } else {
            return store.dispatch(SearchLogic.build404ErrorMessage());
          }
        });
    } else if (isPrettySearchOrSlashSearchPath(location.pathname)) {
      return store.dispatch(searchFetch({ location: normalizeSearchLocation(location), keepParams: true, isSearchHappeningServerSide: true }));
    } else {
      return store.dispatch(SearchLogic.build404ErrorMessage());
    }
  }

  static afterFetchDataOnServer({ dispatch, getState, doProcessReceivedSearchResponse = processReceivedSearchResponse }) {
    const { products: { deferredSearchResponse } } = getState();
    if (!deferredSearchResponse) {
      return;
    }
    const { response, parsedParams, completeUrl } = deferredSearchResponse;
    doProcessReceivedSearchResponse(response, dispatch, getState, parsedParams, completeUrl);
  }

  componentDidMount() {
    const {
      location,
      setHFSearchTerm,
      setLayoutOption,
      filters,
      fetchSearchInlineRecos,
      clearInlineRecos,
      setOosMessaging,
      makeScrollhandler,
      decorateSearchResponse,
      fetchSearchInlineVerticalRecos,
      clearInlineVerticalRecos,
      getHearts,
      isCustomer,
      pageTypeChange,
      fetchSponsoredProducts,
      fetchSymphonySearchComponents,
      getHeartCounts,
      triggerAssignment,
      hydraAds,
      hydraTopBannerAds,
      hydraInlineBannerAds,
      hydraBothBannerAds,
      filters: { executedSearchUrl: filtersExecutedSearchUrl, term },
      products: { executedSearchUrl, list, allProductsCount, inlineRecos, inlineVerticalRecos, isDecorated, isBlacklisted }, toggleFacetableView, params: { seoName } } = this.props;

    triggerAssignment(HYDRA_ADS);
    triggerAssignment(HYDRA_BANNER_ADS_SEARCH);

    if (hasAds && hydraAds && !isCustomer) {
      fetchSponsoredProducts();
    }

    if (hasBannerAds && term && (hydraTopBannerAds || hydraInlineBannerAds || hydraBothBannerAds)) {
      fetchSymphonySearchComponents();
    }

    // Watch scrolling for scrollToTop
    if (makeScrollhandler && this.hasProductResults()) {
      makeScrollhandler();
    }

    pageTypeChange('search');
    toggleFacetableView(true);

    const hasProducts = !!allProductsCount;
    const urlSearchPageParam = this.getPageFromUrlSearch(location.search);

    // if they we redirected to search due to an oos item
    if (location.query && location.query.oosRedirected) {
      setOosMessaging(oosMessaging);
      return false;
    }

    if (!isBlacklisted && shouldSearchServiceBeCalled(location, seoName, executedSearchUrl)) {
      this.requestSearchResultsForLocation({ location, isFresh: true });
    }

    if (locationLooksLikeNonNullQueryStringSearch(location) && executedSearchUrl) {
      this.updateUrl(executedSearchUrl);
    }

    if (setLayoutOption) {
      setLayoutOption('showBackToTop');
    }

    if (!this.loaded && filtersExecutedSearchUrl) {
      this.loaded = true;
    }

    if (filters.originalTerm) {
      setHFSearchTerm(filters.originalTerm);// For Marty HF
    }

    if (this.shouldFetchSearchInlineRecos() && filters.originalTerm && hasProducts && !urlSearchPageParam) {
      fetchSearchInlineRecos(filters.originalTerm);
    } else if (inlineRecos) {
      clearInlineRecos();
    }

    if (this.shouldFetchSearchInlineVerticalRecos() && hasProducts && !urlSearchPageParam) {
      fetchSearchInlineVerticalRecos(filters);
    } else if (inlineVerticalRecos) {
      clearInlineVerticalRecos();
    }

    if (hasProducts && !isDecorated) {
      decorateSearchResponse(list);
    }

    if (allowMoveToFavorites) {
      // Get hearting list
      if (isCustomer) {
        const heartOnLoad = location.query && location.query.heartOnLoad ? location.query.heartOnLoad : null;
        getHearts(heartOnLoad);
      }

      // Get hearting count
      if (hasProducts) {
        getHeartCounts(list);
      }
    }
  }

  componentDidUpdate(prevProps) {
    const {
      filters: prevFilters,
      filters: { term: prevTerm, applySavedFilters: prevSavedFilters },
      location,
      setHFSearchTerm,
      fetchSearchInlineRecos,
      clearInlineRecos,
      products,
      fetchSearchInlineVerticalRecos,
      clearInlineVerticalRecos,
      setOosMessaging,
      getHeartCounts
    } = prevProps;
    const {
      filters,
      filters: { staleProducts: stale, term, originalTerm, executedSearchUrl: filtersExecutedSearchUrl, applySavedFilters },
      location: nextLocation,
      products: {
        executedSearchUrl,
        requestedUrl,
        inlineRecos,
        inlineVerticalRecos,
        list,
        isDecorated
      },
      fetchSymphonySearchComponents,
      hydraTopBannerAds,
      hydraInlineBannerAds,
      hydraBothBannerAds
    } = this.props;

    const currentUrl = location.pathname + location.search;
    const nextUrl = nextLocation.pathname + nextLocation.search;
    const nextUrlSearchPageParam = this.getPageFromUrlSearch(nextLocation.search);
    const isNewUrl = nextUrl !== currentUrl;
    const nextUrlWithoutAppRoot = stripAppRoot(nextUrl);
    const isCurrentUrlMartyPrefixed = currentUrl.indexOf('/marty/') === 0;
    const isNewVertRecos = inlineVerticalRecos && inlineVerticalRecos !== products.inlineVerticalRecos;
    const isNewHorizRecos = inlineRecos && inlineRecos !== products.inlineRecos;
    const isNewProducts = list && list.length && list !== products.list && !isDecorated;
    const isNewOriginalTerm = this.props.filters.originalTerm !== originalTerm;
    logDebug(nextUrl, currentUrl, requestedUrl, executedSearchUrl);

    if (isNewOriginalTerm || (!this.loaded && filtersExecutedSearchUrl)) {
      this.loaded = true;
    }

    if (stale && !searchUrlMatchesFilters(requestedUrl, filters)) {
      // facet/sort/page update
      // not yet requested
      logDebug('stale products due to facet update');
      const [pathname, search] = makeSearchUrl(filters).split(/\?/, 2);
      const mockLocation = { pathname, search: search ? `?${search}` : '' };
      this.requestSearchResultsForLocation({ location: mockLocation });
      setOosMessaging(null);
      this.mobileScrollToTop();
    } else if (isNewUrl && !ZSO_URL_RE.test(nextUrl) && !SEO_URL_RE.test(nextUrl) && requestedUrl !== nextUrlWithoutAppRoot) {
      // the url is changing to either a typed search url or another non-zso url,
      // but we haven't requested it yet.
      logDebug('stale typed or enhanced autcomplete search');
      this.requestSearchResultsForLocation({ location: nextLocation });
    } else if (isNewUrl && (ZSO_URL_RE.test(nextUrl) || SEO_URL_RE.test(nextUrl)) && executedSearchUrl !== nextUrlWithoutAppRoot) {
      // url changed to a zso or fancy typed search, which is not updating theurl to be the canonical.
      // this must be the back button, or a new search link on the page (i.e header nav).
      // this is a case where we will take the url and update search results based on it.
      logDebug('to zso/seo');
      this.requestSearchResultsForLocation({ location: nextLocation });

      this.mobileScrollToTop();
    } else if (executedSearchUrl && executedSearchUrl !== nextUrlWithoutAppRoot) {
      // we haven't updated the display url yet to match facets, but we have
      // one to update to, so update the display url.
      if (ZSO_URL_RE.test(executedSearchUrl)) {
        logDebug('canonicalizable zso');
        if ((nextUrlWithoutAppRoot === requestedUrl && requestedUrl !== executedSearchUrl) || lookLikeSameSearchUrls(executedSearchUrl, nextUrlWithoutAppRoot)) {
          // we submitted either a zso, a naked search url, or a brand url to search, and what was actually executed was slightly different
          // We don't want to push onto the stack since the two URLs are the same (with different history breadcrumbs)
          this.updateUrl(executedSearchUrl);
        } else {
          const forcePushUrl = isCurrentUrlMartyPrefixed ? `/marty${executedSearchUrl}` : executedSearchUrl;
          // if infinite scrolling, we don't want to update the url
          if (hasInfinitePagination && forcePushUrl.includes('p=')) {
            return;
          }
          // this is the executed url for a zso search for which we have not
          // updated the url at all yet. So push on a new url
          this.context.router.forceBrowserPush(forcePushUrl);
        }
      } else if (executedSearchUrl !== location.pathname) {
        // respect the oosRedirected flag and do not get rid of it, it won't appear in the backend since it's a front-end only flag.

        // this is a typed search going from /search?term=foo to /foo
        // so just replace the url since a history entry was already added
        // for this url
        logDebug('canonicalizable typed search');
        this.updateUrl(executedSearchUrl);
      }
    }

    if (applySavedFilters !== prevSavedFilters) {
      this.requestSearchResultsForLocation({ location: nextLocation });
    }

    if (prevFilters.originalTerm !== filters.originalTerm) {
      // keep the header term in sync with the searched for term.
      setHFSearchTerm(filters.originalTerm);// For Marty HF
    }

    if (this.shouldFetchSearchInlineRecos(nextLocation.pathname) && isNewUrl && !nextUrlSearchPageParam) {
      fetchSearchInlineRecos(filters.originalTerm);
    } else if (inlineRecos && isNewUrl) {
      clearInlineRecos();
    }

    if (this.shouldFetchSearchInlineVerticalRecos(nextLocation.pathname) && isNewUrl && !nextUrlSearchPageParam) {
      fetchSearchInlineVerticalRecos(filters);
    } else if (inlineVerticalRecos && isNewUrl) {
      clearInlineVerticalRecos();
    }

    if (
      (
        (inlineRecos && !products.inlineRecos)// New horiz recos we didnt have before
        ||
        (inlineVerticalRecos && !products.inlineVerticalRecos)// New vert recos we didnt have before
      ) && !isNewUrl) {// Haven't changed the page
      this.trackRecoImpressionsDebounced();
    }

    // Get hearting count if applicable
    if ((ExecutionEnvironment.canUseDOM) &&
      (
        isNewProducts ||
        isNewVertRecos ||
        isNewHorizRecos
      )
    ) {
      const productList = [];
      if (list && list.length) {
        productList.push(...list);
      }
      if (inlineVerticalRecos) {
        productList.push(...inlineVerticalRecos);
      }
      if (inlineRecos && inlineRecos.recos) {
        productList.push(...inlineRecos.recos);
      }

      // Finally send off the list
      if (isNewProducts) {
        // Get right away for product list
        getHeartCounts(productList);
      } else {
        // Debounce for recos
        this.getHeartCountsDebounced(productList);
      }
    }
    if (term && prevTerm !== term) {
      if (hasBannerAds && (hydraTopBannerAds || hydraInlineBannerAds || hydraBothBannerAds)) {
        fetchSymphonySearchComponents();
      }
    }
  }

  componentWillUnmount() {
    // if we're going to a product page and the filters are open, the user is
    // probably trying to close the filters and view the search results
    const { setLayoutOption, toggleFacetableView } = this.props;

    toggleFacetableView(false);

    if (setLayoutOption) {
      setLayoutOption('showBackToTop', 0);
    }

    this.loaded = false;
  }

  // Search loaded
  loaded = false;

  requestSearchResultsForLocation = ({ location, isFresh, bypassCache, shouldAppendResults }) => {
    const { fetchFromSearch, fetchFromZso } = this.props;
    if (ZSO_URL_RE.test(location.pathname)) {
      return fetchFromZso({ location, isFresh, bypassCache, shouldAppendResults });
    } else {
      return fetchFromSearch({ location: normalizeSearchLocation(location), isFresh, bypassCache, shouldAppendResults });
    }
  };

  updateUrl = url => {
    let newUrl = url;
    const { hydraSearchColorSwatches, cookies } = this.props;
    if (hydraSearchColorSwatches && cookies[SEARCH_COLOR_SWATCHES_COOKIE] !== 'off' && !url.includes('plsr=1')) {
      newUrl = appendQuery(url, 'plsr=1');
    }
    this.context.router.replacePreserveAppRoot(newUrl);
  };

  mobileScrollToTop = () => !isDesktop(marketplace) && this.scrollToTop();

  trackRecoImpressionsDebounced = debounce(() => {
    const { products: { inlineRecos, inlineVerticalRecos } } = this.props;
    const recommendationImpression = [];
    if (inlineRecos) {
      recommendationImpression.push({
        numberOfRecommendations: inlineRecos.recos ? inlineRecos.recos.length : 0,
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendationSource: 'EP13N',
        widgetType: 'SIMILAR_PRODUCT_WIDGET'
      });
    }
    if (inlineVerticalRecos) {
      recommendationImpression.push({
        numberOfRecommendations: inlineVerticalRecos.length,
        recommendationType: 'PRODUCT_RECOMMENDATION',
        recommendationSource: 'ZAPPOS_DATA_SCIENCE',
        widgetType: 'SIMILAR_PRODUCT_WIDGET'
      });
    }
    track(evRecommendationImpressionWrapper, { recommendationImpression });
  }, 350);

  getHeartCountsDebounced = debounce((...args) => {
    /*
      Debouncing because we may have horiz/vert inline recos to get counts for.
      But let's not wait too long.
    */
    this.props.getHeartCounts(...args);
  }, 350);

  getPageFromUrlSearch = search => {
    const nextUrlSearchParsed = parse(search);
    return nextUrlSearchParsed && nextUrlSearchParsed.p ? parseInt(nextUrlSearchParsed.p, 10) : null;
  };

  scrollToTop = () => {
    window.scrollTo(0,0);
  };

  shouldFetchSearchInlineRecos = (pathname = this.props.location.pathname) => {
    const { filters: { page } } = this.props;
    const doesNotHaveZsoFilters = !ZSO_URL_WITH_FILTERS_RE.test(pathname);
    const doesNotHaveSlashFilters = !SLASH_SEARCH_RE.test(pathname);
    return showHorizontalInlineSearchRecos && doesNotHaveZsoFilters && doesNotHaveSlashFilters && ExecutionEnvironment.canUseDOM && page === 0;
  };

  shouldFetchSearchInlineVerticalRecos = (pathname = this.props.location.pathname) => {
    const { hydraSearchInlineVerticalRecos, filters: { term, page, sort }, products: { totalProductCount } } = this.props;
    const doesNotHaveZsoFilters = !ZSO_URL_WITH_FILTERS_RE.test(pathname);
    return hydraSearchInlineVerticalRecos && showVerticalInlineSearchRecos && term && term !== 'no-term' && page === 0 && !Object.keys(sort).length && totalProductCount >= 4 && doesNotHaveZsoFilters && ExecutionEnvironment.canUseDOM;
  };

  hasProductResults = () => {
    const { products: { allProductsCount } } = this.props;
    return allProductsCount > 0;
  };

  shouldShowNoResults = () => {
    const { products } = this.props;
    return !!products.requestedUrl && !products.isLoading && !this.hasProductResults();
  };

  handleColorSwatchToggle = ({ target: { checked } }) => {
    const { location, setAndStoreCookie } = this.props;
    const expiration = sessionExpiration();
    const newCookie = checked ? 'on' : 'off';
    setAndStoreCookie(SEARCH_COLOR_SWATCHES_COOKIE, newCookie, expiration);
    trackEvent('TE_SEARCH_RELATED_STYLES_TOGGLE', `${checked}`);
    this.requestSearchResultsForLocation({ location, bypassCache: true });
  };

  render() {
    const { handleColorSwatchToggle, hasProductResults, requestSearchResultsForLocation, shouldShowNoResults } = this;
    const { sponsoredProducts, hydraAds, hydraTopBannerAds, hydraInlineBannerAds, hydraBothBannerAds, isCustomer } = this.props;
    return (
     <>
       {this.shouldShowNoResults() && <NoSearchResults />}
       {this.props.children({
         handleColorSwatchToggle,
         hasProductResults,
         requestSearchResultsForLocation,
         shouldShowNoResults,
         sponsoredProducts,
         hydraAds,
         hydraTopBannerAds,
         hydraInlineBannerAds,
         hydraBothBannerAds,
         isCustomer
       })}
     </>
    );
  }
}

SearchLogic.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

SearchLogic.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func.isRequired
};

SearchLogic.propTypes = {
  products: PropTypes.object.isRequired,
  filters: PropTypes.object,
  facets: PropTypes.object
};

function mapStateToProps(state) {
  const { cookies, products, facets, filters, isFacetsVisible, search: { sponsoredProducts } } = state;
  const isCustomer = !!(ExecutionEnvironment.canUseDOM && state.cookies['x-main']);

  return {
    cookies,
    products,
    facets,
    filters,
    isFacetsVisible,
    isCustomer,
    hydraSearchInlineVerticalRecos: isAssigned(HYDRA_SEARCH_INLINE_VERTICAL_RECOS, 1, state),
    hydraAds: isAssigned(HYDRA_ADS, 1, state),
    hydraTopBannerAds: isAssigned(HYDRA_BANNER_ADS_SEARCH, 1, state),
    hydraInlineBannerAds: isAssigned(HYDRA_BANNER_ADS_SEARCH, 2, state),
    hydraBothBannerAds: isAssigned(HYDRA_BANNER_ADS_SEARCH, 3, state),
    sponsoredProducts
  };
}

export const mapDispatchToProps = {
  redirectWithAppRoot,
  toggleFacetsContainer,
  toggleFacetableView,
  fetchSearchInlineRecos,
  clearInlineRecos,
  fetchSearchInlineVerticalRecos,
  clearInlineVerticalRecos,
  setOosMessaging,
  decorateSearchResponse,
  getHearts,
  pageTypeChange,
  getHeartCounts,
  setHFSearchTerm,
  setAndStoreCookie,
  sessionExpiration,
  fetchFromZso,
  fetchFromSearch,
  fetchSponsoredProducts,
  fetchSymphonySearchComponents,
  triggerAssignment
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchLogic);
